import React, { useCallback, useState } from 'react';
import {
  Box,
  FieldWrapper,
  FormPreloader,
  Label,
  OptionKey,
  RawButton,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE, ERROR_ELEMENT_SCROLL_CLASS_NAME } from '@/common/constants';

import FieldLevelAccessCreateModal from '../FieldLevelAccessSetup/FieldLevelAccessCreateModal';

import FieldLevelAccessGroup from './FieldLevelAccessGroup';
import { useFieldLevelAccessListQuery } from './queries';

interface Props {
  defaultId?: string;
  isEditMode?: boolean;
  value?: OptionKey;
  errorText?: string;
  onChangeValue: (value: OptionKey) => void;
}

const FieldLevelAccessDropdown: React.FC<Props> = ({
  errorText,
  defaultId,
  isEditMode,
  value,
  onChangeValue,
}) => {
  const [expanded, setExpanded] = useState(true);
  const handleToggleExpand = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const { options, currentFieldLevelAccess, isLoading, refetch } = useFieldLevelAccessListQuery({
    id: value || defaultId,
  });
  if (isLoading) {
    return <FormPreloader count={2} />;
  }
  if (!isEditMode) {
    return (
      <FieldWrapper>
        <Label>Role is applicable to</Label>
        <Box>
          <Text weight={400} data-testId="field-level-access_text">
            {currentFieldLevelAccess?.name || EMPTY_FIELD_VALUE}
          </Text>
          <Text data-testId="field-level-access_description" size="small" color="textSecondary">
            {currentFieldLevelAccess?.description || ''}
          </Text>
        </Box>
      </FieldWrapper>
    );
  }

  return (
    <Box>
      <FieldWrapper>
        <Label showRequireIcon>Field Level Access</Label>
        <Box width="medium" direction="row">
          <SelectDropdown
            errorText={errorText}
            placeholder="Select access"
            options={options}
            singleMode
            testId="field-level-access"
            value={value}
            onChange={onChangeValue}
            id="field-level-access"
            name="field_level-access"
          />
          <Box margin={{ left: 's' }} className={errorText ? ERROR_ELEMENT_SCROLL_CLASS_NAME : ''}>
            <FieldLevelAccessCreateModal setLevelAccessValue={onChangeValue} refetch={refetch} />
          </Box>
        </Box>
      </FieldWrapper>
      {currentFieldLevelAccess && (
        <Box direction="column" pad={{ top: 'l' }}>
          <RawButton onClick={handleToggleExpand}>
            <Text color="textAccent" weight={400}>
              Collapse all
            </Text>
          </RawButton>
          <FieldLevelAccessGroup
            testId="normal-properties"
            defaultExpanded={expanded}
            name="Normal Organization Properties"
            items={currentFieldLevelAccess.properties}
          />

          <FieldLevelAccessGroup
            isOverridableProperties
            testId="override-properties"
            defaultExpanded={expanded}
            name="Overridable Organization Properties"
            items={currentFieldLevelAccess.overridableProperties}
          />
        </Box>
      )}
    </Box>
  );
};

export default FieldLevelAccessDropdown;
